import { useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ScheduledArchivationAlert } from '@/components/scheduled-archivation-alert';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form } from '@/components/ui/form';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { DatePicker } from '@/modules/common/components/molecules/date/date-picker';
import { invalidateProjectQueries } from '@/modules/common/utils/query-invalidation';
import { getAvailableUnarchivationDate, unarchiveProject } from '@/modules/projects';
import { Project } from '@/modules/projects/types/project';
import { formatDisplayDate, getOptimalPresetDate } from '@/utils/date-formatters';
import { normalizeNullableToStartOfDayUTC } from '@/utils/date-formatters/date-formatters';

interface UnarchiveProjectModalProps {
  project: Project;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const unarchiveSchema = z.object({
  unarchiveDate: z.date({
    required_error: 'authenticated.projects:archive.unarchiveDateRequired',
  }),
});

type UnarchiveFormData = z.infer<typeof unarchiveSchema>;

/**
 * A streamlined project unarchiving modal for restoring archived projects to active status.
 * Provides simple confirmation workflow with clear messaging about restoration process.
 *
 * **Key Features:**
 * - **Simple Confirmation**: Single-click unarchive with confirmation dialog
 * - **Dual State Management**: Supports both internal and external modal state control
 * - **Clear Messaging**: Explains what happens when unarchiving projects
 * - **API Integration**: Handles async unarchive operations with proper error handling
 * - **Data Consistency**: Invalidates relevant query caches after successful unarchiving
 * - **Flexible Triggers**: Supports custom triggers or falls back to default button
 * - **Loading States**: Shows proper loading feedback during API operations
 *
 * **Unarchive Process:**
 * 1. User confirms unarchive action
 * 2. API call restores project to active status
 * 3. Success invalidates project caches and closes modal
 * 4. Error keeps modal open for retry
 *
 * **Restoration Effects:**
 * - Project becomes visible in active project lists
 * - Time tracking can be resumed
 * - All historical data remains intact
 * - Team assignments are restored
 *
 * @example
 * ```tsx
 * // Basic usage with internal state management
 * <UnarchiveProjectModal
 *   project={archivedProject}
 *   onSuccess={() => {
 *     toast.success('Project restored successfully');
 *     navigate('/projects');
 *   }}
 * />
 *
 * // With custom trigger button
 * <UnarchiveProjectModal
 *   project={project}
 *   trigger={
 *     <Button variant="outline" size="sm">
 *       <ArchiveRestore className="w-4 h-4 mr-2" />
 *       Restore Project
 *     </Button>
 *   }
 *   onSuccess={() => queryClient.invalidateQueries(['projects'])}
 * />
 *
 * // In archived projects list
 * function ArchivedProjectsList() {
 *   const { data: archivedProjects } = useQuery(['projects', 'archived'], getArchivedProjects);
 *
 *   return (
 *     <div className="space-y-4">
 *       {archivedProjects?.map(project => (
 *         <div key={project.id} className="flex items-center justify-between p-4 border rounded">
 *           <div>
 *             <h3>{project.name}</h3>
 *             <p className="text-sm text-muted-foreground">
 *               Archived on {format(project.archivedAt, 'MMM dd, yyyy')}
 *             </p>
 *           </div>
 *           <UnarchiveProjectModal
 *             project={project}
 *             onSuccess={() => {
 *               toast.success(`${project.name} restored`);
 *               queryClient.invalidateQueries(['projects']);
 *             }}
 *           />
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 *
 * // External state management for complex workflows
 * function ProjectManagement() {
 *   const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
 *   const [selectedProject, setSelectedProject] = useState<Project | null>(null);
 *
 *   const handleUnarchiveClick = (project: Project) => {
 *     setSelectedProject(project);
 *     setShowUnarchiveModal(true);
 *   };
 *
 *   return (
 *     <>
 *       <ArchivedProjectsList onUnarchive={handleUnarchiveClick} />
 *
 *       {selectedProject && (
 *         <UnarchiveProjectModal
 *           project={selectedProject}
 *           open={showUnarchiveModal}
 *           onOpen={() => setShowUnarchiveModal(true)}
 *           onClose={() => {
 *             setShowUnarchiveModal(false);
 *             setSelectedProject(null);
 *           }}
 *           onSuccess={() => {
 *             toast.success('Project restored to active status');
 *             setShowUnarchiveModal(false);
 *             setSelectedProject(null);
 *             queryClient.invalidateQueries(['projects']);
 *           }}
 *         />
 *       )}
 *     </>
 *   );
 * }
 *
 * // Bulk unarchive operations
 * function BulkUnarchiveProjects() {
 *   const [selectedProjects, setSelectedProjects] = useState<Project[]>([]);
 *
 *   const handleBulkUnarchive = async () => {
 *     const promises = selectedProjects.map(project =>
 *       unarchiveProject(project.id)
 *     );
 *
 *     try {
 *       await Promise.all(promises);
 *       toast.success(`${selectedProjects.length} projects restored`);
 *       queryClient.invalidateQueries(['projects']);
 *       setSelectedProjects([]);
 *     } catch (error) {
 *       toast.error('Some projects failed to restore');
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleBulkUnarchive} disabled={selectedProjects.length === 0}>
 *       Restore Selected ({selectedProjects.length})
 *     </Button>
 *   );
 * }
 *
 * // With analytics and error tracking
 * <UnarchiveProjectModal
 *   project={project}
 *   onSuccess={() => {
 *     analytics.track('project_unarchived', {
 *       projectId: project.id,
 *       projectName: project.name,
 *       archivedDuration: Date.now() - project.archivedAt?.getTime()
 *     });
 *     toast.success('Project restored successfully');
 *   }}
 * />
 * ```
 */
export function UnarchiveProjectModal({
  project,
  onSuccess,
  trigger,
  open,
  onOpen,
  onClose,
}: UnarchiveProjectModalProps) {
  const { t } = useTranslation(['authenticated.projects', 'common']);
  const queryClient = useQueryClient();

  const form = useForm<UnarchiveFormData>({
    resolver: zodResolver(unarchiveSchema),
    mode: 'onChange',
    defaultValues: {
      unarchiveDate: undefined,
    },
  });

  const unarchiveProjectMutation = useMutation({
    mutationFn: ({ projectId, unarchiveDate }: { projectId: string; unarchiveDate: Date }) =>
      unarchiveProject({ projectId, unarchiveDate }),
    onSuccess: async () => {
      await invalidateProjectQueries(queryClient);
      await queryClient.refetchQueries({ queryKey: ['project', project.id.toString()] });
      form.reset();
      onSuccess?.();
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine the actual modal open state (external or internal)
  const actualModalOpen = open !== undefined ? open : isModalOpen;

  // Fetch available unarchivation date for date validation
  const {
    data: availableUnarchiveDate,
    isLoading: isLoadingAvailableUnarchiveDate,
    error: availableUnarchiveDateError,
  } = useQuery({
    queryKey: ['available-unarchive-date', project.id],
    queryFn: () => getAvailableUnarchivationDate(project.id.toString()),
    enabled: actualModalOpen,
  });

  // Determine effective minimum unarchive date
  const effectiveMinUnarchiveDate = useMemo(() => {
    if (availableUnarchiveDateError || availableUnarchiveDate === null) {
      // If there's an error or null, default to today
      return normalizeNullableToStartOfDayUTC(new Date());
    }
    return availableUnarchiveDate;
  }, [availableUnarchiveDate, availableUnarchiveDateError]);

  // Pre-set the optimal date once loaded
  useEffect(() => {
    if (!form.getValues('unarchiveDate') && effectiveMinUnarchiveDate) {
      const dateToSet = getOptimalPresetDate(effectiveMinUnarchiveDate, null);
      form.setValue('unarchiveDate', dateToSet, { shouldValidate: true });
    }
  }, [effectiveMinUnarchiveDate, form]);

  const handleUnarchive = async (closeModal: () => void) => {
    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }

    const formData = form.getValues();

    try {
      await unarchiveProjectMutation.mutateAsync({
        projectId: project.id.toString(),
        unarchiveDate: formData.unarchiveDate,
      });
      closeModal();
    } catch (error) {
      // Error handled by mutation, keep modal open for retry
    }
  };

  // Function to disable dates before the minimum unarchive date
  const isDateDisabled = (date: Date): boolean => {
    if (!effectiveMinUnarchiveDate) return false;

    // Disable dates that are before the minimum unarchive date
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const minUnarchiveOnly = new Date(
      effectiveMinUnarchiveDate.getFullYear(),
      effectiveMinUnarchiveDate.getMonth(),
      effectiveMinUnarchiveDate.getDate()
    );

    return dateOnly < minUnarchiveOnly;
  };

  const unarchiveDate = form.watch('unarchiveDate');
  const isFormValid = !!unarchiveDate;

  const messageContent = (
    <div className="space-y-4">
      {availableUnarchiveDateError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-5 w-5" />
          <AlertDescription>
            {t('authenticated.projects:archive.unableToLoadUnarchivationDate')}
          </AlertDescription>
        </Alert>
      )}
      {project.nextUnarchiveDate && (
        <ScheduledArchivationAlert
          message={t('authenticated.projects:scheduledArchive.willBeUnarchivedOn', {
            date: formatDisplayDate(project.nextUnarchiveDate),
          })}
          className="mb-0"
        />
      )}
      <div>
        <Trans
          i18nKey="authenticated.projects:archive.unarchiveProjectMessage"
          components={{ strong: <strong /> }}
        />
      </div>
    </div>
  );

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.projects:archive.unarchiveProjectTitle')}
      message={messageContent}
      confirmText={
        unarchiveProjectMutation.isPending
          ? t('authenticated.projects:archive.unarchiving')
          : t('authenticated.projects:archive.unarchiveProject')
      }
      confirmVariant="default"
      onConfirm={handleUnarchive}
      isLoading={unarchiveProjectMutation.isPending}
      disabled={!isFormValid || isLoadingAvailableUnarchiveDate || !!availableUnarchiveDateError}
      open={actualModalOpen}
      onOpen={onOpen || (() => setIsModalOpen(true))}
      onClose={
        onClose ||
        (() => {
          setIsModalOpen(false);
          form.reset();
        })
      }
    >
      <Form {...form}>
        <form className="space-y-4">
          <DatePicker
            name="unarchiveDate"
            control={form.control}
            label={t('authenticated.projects:archive.unarchiveDate')}
            dateFormat="D MMM, YYYY"
            disabled={isDateDisabled}
            inputDisabled={isLoadingAvailableUnarchiveDate}
            isLoading={isLoadingAvailableUnarchiveDate}
          />
        </form>
      </Form>
    </ConfirmationModal>
  );
}
